import { Injectable } from '@angular/core';
import {
    Http,
    Headers
} from 'app/services/http';
import { AuthService } from 'app/services/authentication/auth.service';
import { LoggerService } from 'app/logger/logger.service';
import { GlobalVariables, CONTENT_TYPE, AUTHORIZATION, WORKSPACE_APIVERSION } from 'app/services/globals';
import { FactoryUrlInformationService } from 'app/services/factory-url-information.service';
import { convertObjectToString, format } from 'app/shared/utils/string.util';

import * as HttpUtil from 'app/shared/utils/http.utils';
import { tryExtractErrorResponseFromRP } from 'app/shared/utils/http-response.util';
import { WorkspaceUrlInformationService } from './workspace-url-information.service';
import { UrlInformationBaseService } from './url-information-base.service';
import { AuthConstants } from './authentication/auth-constants';
import { retryWhen } from 'rxjs/operators';
import { ExposureControlService } from './exposure-control/base-exposure-control.service';
import { ExposureControlType } from './exposure-control/ec.typings';

export interface IDataPlaneAuthService {
    getDataPlaneAccess: () => Promise<IDataPlaneAuthResponse>;
    canGetDataPlaneAccess: () => Promise<boolean>;
}

export interface IDataPlaneUserAccessPolicy {
    permissions: string;
    accessResourcePath: string;
    profileName: string;
    startTime: string;
    expireTime: string;
}

export interface IDataPlaneAuthResponse {
    policy: IDataPlaneUserAccessPolicy;
    dataPlaneUrl: string;
    accessToken: string;
}

@Injectable({
    providedIn: 'root'
})
export class DataPlaneAuthService implements IDataPlaneAuthService {
    private static className: string = 'DataPlaneAuthService';
    private static readonly accessRenewTime: number = 30 * 60 * 1000; // Set to half hour
    private static readonly dataPlaneAuthorizationFeatureName = 'ADFFactoryDataPlaneAuthrization';

    private dataPlaneAuthInfo: IDataPlaneAuthResponse;
    private isDataPlaneAuthEnabled: boolean;
    private requestingAccess = false;
    private dataPlaneAccessPromise: Promise<IDataPlaneAuthResponse>;

    private _urlService: UrlInformationBaseService;

    constructor(private readonly _httpClient: Http,
        readonly factoryUrlService: FactoryUrlInformationService,
        readonly workspaceUrlService: WorkspaceUrlInformationService,
        private readonly _authService: AuthService,
        private readonly _exposureControleService: ExposureControlService,
        private readonly _logger: LoggerService) {
        this._urlService = __IS_A365__ ? workspaceUrlService : factoryUrlService;
    }

    public async canGetDataPlaneAccess(): Promise<boolean> {
        if (this.isDataPlaneAuthEnabled == null) {
            // Check if DataPlaneAuth EC feature is enabled
            const isDPAuthEnabledInEC = await this._exposureControleService.getFeatureValue({ featureName: DataPlaneAuthService.dataPlaneAuthorizationFeatureName, featureType: ExposureControlType.Feature });
            if (!isDPAuthEnabledInEC) {
                this._logger.log(DataPlaneAuthService.className, `DataPlane is not enabled in EC: returned EC feature value: ${isDPAuthEnabledInEC}`);
            }
            this.isDataPlaneAuthEnabled = !!isDPAuthEnabledInEC;
        }

        return this.isDataPlaneAuthEnabled;
    }

    public async getDataPlaneAccess(): Promise<IDataPlaneAuthResponse> {
        // get DP access info from memory -> if not valid, get from RP
        if (!this.isAccessValid()) {
            // request DataPlane access from RP
            this.dataPlaneAuthInfo = await this.requestAccess();
        }

        return this.dataPlaneAuthInfo;
    }

    private isAccessValid(): boolean {
        if (!this.dataPlaneAuthInfo) {
            return false;
        }

        // check if access expired or will expire soon
        const now = new Date();
        const expireTime = new Date(this.dataPlaneAuthInfo.policy.expireTime);
        if (expireTime.getTime() - now.getTime() > DataPlaneAuthService.accessRenewTime) {
            return true;
        }
        return false;
    }

    private async requestAccess(): Promise<IDataPlaneAuthResponse> {
        if (!this.requestingAccess) {
            this.requestingAccess = true;
            this.dataPlaneAccessPromise = this.requestDataPlaneAccess();
        }
        const authInfo = await this.dataPlaneAccessPromise;
        return authInfo;
    }

    private async requestDataPlaneAccess(): Promise<IDataPlaneAuthResponse> {
        this._logger.log(DataPlaneAuthService.className, 'Requesting DataPlane access');
        const token = await this._authService.getToken();
        const apiVersion = __IS_A365__ ? WORKSPACE_APIVERSION : GlobalVariables.dataFactoryVersion;
        const rpGetDataPlaneAccessUrl = `${AuthConstants.RESOURCE_MANAGER_ENDPOINT}${this._urlService.getResourceId()}/getDataPlaneAccess?api-version=${apiVersion}`;
        const headers = new Headers();
        headers.append(CONTENT_TYPE, 'application/json');
        headers.append(AUTHORIZATION, `Bearer ${token}`);
        try {
            const response = (await this._httpClient.post(rpGetDataPlaneAccessUrl, null, { headers }).pipe(retryWhen(HttpUtil.retryPolicyForConflictAndServerError)).toPromise());
            this._logger.log(DataPlaneAuthService.className, 'Received DataPlane access');
            const authInfo = response.json() as IDataPlaneAuthResponse;
            authInfo.dataPlaneUrl = `${authInfo.dataPlaneUrl}/`;
            this.requestingAccess = false;
            return authInfo;
        } catch (error) {
            this._logger.error(DataPlaneAuthService.className, `Failed to get DataPlane access, reason: ${convertObjectToString(error)}`);
            this.requestingAccess = false;
            const errorResponseString = convertObjectToString(tryExtractErrorResponseFromRP(error));
            const message = error.status === 403 ? format(stringResources.DoNotHavePermissionToGetDataPlaneAccess, errorResponseString) :
                format(stringResources.GetDataPlaneAccessUnknownError, errorResponseString);
            throw new Error(message);
        }
    }
}
